import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onlyUniqueFilter, stringToColour } from 'shared/helpers/general-helpers';
import { getReasonNameByIdOnly } from 'shared/helpers/reason-helper/reason-helper';
import { GetAnalysisGraphBySiteIdAndDates } from 'shared/state/ducks/dead-piglets-event/operations';
import { WebAppState } from 'web/state/store.web';
import { BarChart } from '../../graph/bar-graph/bar-chart';
import { ViewWeb } from '../../utils/web-view';
import { BasicSwitch } from '../../basic-switch/basic-switch';
import { localized } from 'shared/state/i18n/i18n';
import { FlexRow } from '../../basic-flex/flex-row';

import { getDateString, getStringDate, SubtractDays } from 'shared/helpers/date-helpers';
import { NaturalSort } from 'shared/helpers/natural-sort';
import { Heading } from '../../utils/heading';
import { SkioldDatePicker } from '../../skiold-components/skiold-date-picker/skiold-date-picker';
import { SkioldFetchIcon } from '../../utils/skiold-fetch-icon';
import { SkioldButton } from '../../skiold-components/skiold-button/skiold-button';
import { usePrevious } from 'shared/helpers/redux-helpers';
import { colorArray } from 'shared/assets/root-assets/colors';
import Box from '@mui/material/Box';
export interface PropsFromParent {
	fromDateProp: Date | undefined;
	toDateProp: Date | undefined;
	closeModal?: () => void;
}

export const AnalysisDeparturePigletGraph: FC<PropsFromParent> = React.memo(
	({
		closeModal,
		fromDateProp = SubtractDays(new Date(), 7).withoutTime(),
		toDateProp = new Date().endOfDayTime(),
	}) => {
		const dispatch = useDispatch();
		const analysisData = useSelector((state: WebAppState) => state.deadPigletsEvents.analysisDeadPigletGraphData);
		const [argument, setArgument] = useState<string>('week');
		const prevArgument = usePrevious(argument);
		const [fromDate, setfromDate] = useState<Date>(fromDateProp);
		const [toDate, setToDate] = useState<Date>(toDateProp);
		const [zoom, setZoom] = useState<'none' | 'pan' | 'zoom' | 'both'>('both');
		// Fetch data
		useEffect(() => {
			dispatch(GetAnalysisGraphBySiteIdAndDates(fromDate, toDate));
		}, [fromDate, toDate]);

		// Set Data
		const data = useMemo(() => {
			let objectData = {};
			const valueFields = analysisData.map(x => x.reasonId!).filter(onlyUniqueFilter);
			const defaultObject = {};
			valueFields.forEach(valueField => {
				defaultObject[valueField] = 0;
			});

			// create object
			analysisData.forEach(item => {
				if (item[argument]) {
					const key = item[argument].toString();
					if (!objectData[key]) {
						objectData[key] = { ...defaultObject };
					}
					if (!objectData[key][item.reasonId]) {
						objectData[key][item.reasonId] = item.count;
					} else {
						objectData[key][item.reasonId] += item.count;
					}
					objectData[key].date = item.departureDate;
				}
			});

			// map object to array
			let resultArray = Object.keys(objectData).map(key => {
				let argumentObject = objectData[key];
				if (argumentObject) {
					argumentObject[argument] = argument === 'departureDate' ? getStringDate(key) : key;
				}
				return argumentObject;
			});
			if (argument === 'week' || argument === 'departureDate') {
				return resultArray.slice().sort((a, b) => a.date - b.date);
			}
			return resultArray.slice().sort((a, b) => NaturalSort(a[argument], b[argument]));
		}, [analysisData, argument]);

		const series = useMemo(() => {
			const valueFields = analysisData.map(x => x.reasonId).filter(onlyUniqueFilter);

			return valueFields.map((uniqueValue, index) => {
				return {
					argumentField: argument,
					valueField: uniqueValue,
					name: uniqueValue,
					color: colorArray[index],
					key: uniqueValue,
				} as SeriesProps;
			});
		}, [data, argument, analysisData]);

		//  set YAxis
		const yAxis = useMemo(() => {
			return [{ position: 'right' } as RawAxisProps];
		}, []);

		const keyTranslator = useCallback((key: string) => {
			return getReasonNameByIdOnly(key);
		}, []);
		const options = [
			{ key: 'week', value: localized('week') },
			{ key: 'litter', value: localized('litter') },
			{ key: 'age', value: localized('age') },
			{ key: 'departureDate', value: localized('departureDate') },
		];

		const onChange = useCallback(
			(key: string) => {
				if (key === argument) {
					return;
				}
				setArgument(key);
			},
			[argument],
		);

		useEffect(() => {
			let timeoutID: number;
			if (argument === prevArgument) {
				timeoutID = setTimeout(() => {
					setZoom('both');
				}, 100);
			} else {
				setZoom('none');
			}
			return () => {
				clearTimeout(timeoutID);
			};
		}, [argument, prevArgument, zoom]);
		return (
			<>
				<Heading text={localized('AnalysisDeparturePiglets')} />
				<ViewWeb className="date-picker-container">
					<ViewWeb className="date-picker-container flexOne">
						<SkioldDatePicker
							onDateChanged={setfromDate}
							selectedDate={fromDate}
							maxDate={toDate}
							text={'FromDate'}
							containerClassName="datepicker-margin"
							includeTimeStamp={false}
						/>
						<SkioldDatePicker
							onDateChanged={setToDate}
							selectedDate={toDate}
							isEndDate={true}
							text={'ToDate'}
							containerClassName="datepicker-margin"
						/>
					</ViewWeb>
					<Box sx={{ alignSelf: 'center', flex: 1 }}>
						<BasicSwitch options={options} onChange={onChange} selectedValue={argument} />
					</Box>
					{closeModal && (
						<ViewWeb className="flexOne">
							<ViewWeb>
								<SkioldButton
									className="display-left"
									theme="grey"
									onPress={closeModal}
									title={localized('Close')}
								/>{' '}
							</ViewWeb>
						</ViewWeb>
					)}
				</ViewWeb>
				<FlexRow sx={{ margin: '20px', flexDirection: 'column' }}>
					<BarChart
						barSeries={series}
						chartData={data}
						valueAxis={yAxis}
						title={''}
						stack={true}
						keyTranslator={keyTranslator}
						zoomPan={zoom}
						verticalArgumentLabels={argument === 'departureDate' || argument === 'week'}
					/>
				</FlexRow>
			</>
		);
	},
);
